import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';

// Styled Components
const DrawerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  z-index: 2100;
  animation: fadeIn 0.2s ease-out;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const DrawerContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 520px;
  height: 100vh;
  background: linear-gradient(180deg, #1e293b 0%, #0f172a 100%);
  border-left: 1px solid rgba(148, 163, 184, 0.2);
  z-index: 2200;
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease-out;
  
  @keyframes slideIn {
    from { transform: translateX(100%); }
    to { transform: translateX(0); }
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: rgba(30, 41, 59, 0.8);
  border-bottom: 1px solid rgba(148, 163, 184, 0.15);
`;

const DrawerTitle = styled.h2`
  margin: 0;
  color: #f1f5f9;
  font-size: 1.1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  
  span {
    font-size: 0.9rem;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: 1px solid rgba(148, 163, 184, 0.3);
  color: #94a3b8;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(148, 163, 184, 0.1);
    color: #e2e8f0;
    border-color: rgba(148, 163, 184, 0.5);
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 0;
  padding: 0 20px;
  background: rgba(30, 41, 59, 0.5);
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
`;

const Tab = styled.button<{ $active?: boolean }>`
  padding: 12px 16px;
  background: transparent;
  border: none;
  color: ${props => props.$active ? '#3b82f6' : '#94a3b8'};
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
  
  &:hover {
    color: ${props => props.$active ? '#3b82f6' : '#e2e8f0'};
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${props => props.$active ? '#3b82f6' : 'transparent'};
    border-radius: 2px 2px 0 0;
  }
`;

const SortContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 12px 20px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
`;

const SortLabel = styled.span`
  color: #94a3b8;
  font-size: 0.85rem;
  margin-right: 8px;
`;

const SortSelect = styled.select`
  background: rgba(30, 41, 59, 0.8);
  border: 1px solid rgba(148, 163, 184, 0.25);
  color: #e2e8f0;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 0.85rem;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const AuditList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;
`;

const AuditEntry = styled.div`
  display: grid;
  grid-template-columns: 140px 130px 120px 1fr;
  gap: 12px;
  padding: 14px 20px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.08);
  align-items: start;
  transition: background 0.15s;
  
  &:hover {
    background: rgba(59, 130, 246, 0.05);
  }
`;

const AuditDate = styled.div`
  color: #94a3b8;
  font-size: 0.8rem;
  line-height: 1.4;
`;

const AuditUser = styled.div`
  color: #3b82f6;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const AuditAction = styled.div`
  color: #e2e8f0;
  font-size: 0.85rem;
`;

const AuditDescription = styled.div`
  color: #cbd5e1;
  font-size: 0.85rem;
  line-height: 1.4;
  
  .highlight {
    color: #22c55e;
    font-weight: 500;
  }
  
  .old-value {
    color: #ef4444;
    text-decoration: line-through;
    margin-right: 4px;
  }
  
  .new-value {
    color: #22c55e;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #94a3b8;
  font-size: 0.95rem;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #64748b;
  text-align: center;
  
  .icon {
    font-size: 2.5rem;
    margin-bottom: 12px;
    opacity: 0.5;
  }
`;

const ErrorState = styled.div`
  padding: 20px;
  margin: 20px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  color: #fca5a5;
  text-align: center;
`;

// Helper functions
const formatDateTime = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }) + ' ' + date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const parseJsonSafe = (val: any): any => {
  if (!val) return null;
  if (typeof val === 'object') return val;
  try {
    return JSON.parse(val);
  } catch {
    return null;
  }
};

const formatAction = (log: any): string => {
  const action = log.action || log.method || 'UNKNOWN';
  switch (action.toUpperCase()) {
    case 'CREATE': return 'Created Trade';
    case 'UPDATE': return 'Updated Field';
    case 'SEND': return 'Sent Trade';
    case 'EXECUTE': return 'Executed Trade';
    case 'FIX_EXECUTE': return 'Executed Trade';
    case 'CANCEL': return 'Cancelled Trade';
    case 'APPROVE': return 'Approved Trade';
    case 'REJECT': return 'Rejected Trade';
    case 'VOID': return 'Voided Trade';
    case 'VIEW': return 'Viewed Trade';
    case 'ATTACHMENT': return 'Added Attachment';
    case 'DELETE': return 'Deleted';
    default: return action;
  }
};

const formatDescription = (log: any): React.ReactNode => {
  const after = parseJsonSafe(log.afterState);
  const before = parseJsonSafe(log.beforeState);
  const action = (log.action || log.method || '').toUpperCase();
  
  // Status change
  if (after?.status && before?.status && after.status !== before.status) {
    return (
      <>
        Status from <span className="old-value">{before.status}</span> to <span className="new-value">{after.status}</span>
      </>
    );
  }
  
  // Status in response
  if (after?.status) {
    return <>Status: <span className="highlight">{after.status}</span></>;
  }
  
  // Specific actions
  if (action === 'SEND') {
    return <>Trade sent for execution</>;
  }
  
  if (action === 'EXECUTE' || action === 'FIX_EXECUTE') {
    const price = after?.price || after?.executionPrice;
    const venue = after?.ecnPlatform || after?.executionVenue;
    if (venue && price) {
      return <>Executed via <span className="highlight">{venue}</span> at <span className="highlight">{price}</span></>;
    }
    if (venue) {
      return <>Executed via <span className="highlight">{venue}</span></>;
    }
    return price 
      ? <>Executed at price <span className="highlight">{price}</span></>
      : <>Trade executed</>;
  }
  
  if (action === 'CREATE') {
    const type = after?.transactionType || '';
    const amount = after?.amount || '';
    return amount 
      ? <>{type} - Amount: <span className="highlight">${Number(amount).toLocaleString()}</span></>
      : <>Trade created</>;
  }
  
  // Path-based description
  if (log.path?.includes('/attachments')) {
    return <>Attachment updated</>;
  }
  
  // Generic field update
  if (after && before) {
    const changedFields: string[] = [];
    Object.keys(after).forEach(key => {
      if (after[key] !== before[key] && key !== 'updatedAt' && key !== 'createdAt') {
        changedFields.push(key);
      }
    });
    if (changedFields.length > 0) {
      return <>Updated: {changedFields.slice(0, 3).join(', ')}{changedFields.length > 3 ? '...' : ''}</>;
    }
  }
  
  return <>Record modified</>;
};

interface AuditHistoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  tradeId: number | null;
  ticketNumber?: string;
}

const AuditHistoryDrawer: React.FC<AuditHistoryDrawerProps> = ({
  isOpen,
  onClose,
  tradeId,
  ticketNumber
}) => {
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'trade' | 'notes' | 'attachments'>('all');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  
  useEffect(() => {
    if (isOpen && tradeId) {
      fetchAuditLogs();
    }
  }, [isOpen, tradeId]);
  
  const fetchAuditLogs = async () => {
    if (!tradeId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/api/audit/trade/${tradeId}`);
      setAuditLogs(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load audit history');
    } finally {
      setLoading(false);
    }
  };
  
  // Deduplicate logs - group by timestamp (within 2 seconds) and action
  const deduplicatedLogs = auditLogs.reduce((acc: any[], log: any) => {
    const logTime = new Date(log.eventTime || log.createdAt || 0).getTime();
    const logAction = (log.action || log.method || '').toUpperCase();
    
    // Check if similar entry exists within 2 second window
    const isDuplicate = acc.some(existing => {
      const existingTime = new Date(existing.eventTime || existing.createdAt || 0).getTime();
      const existingAction = (existing.action || existing.method || '').toUpperCase();
      const timeDiff = Math.abs(logTime - existingTime);
      
      // Same action within 2 seconds = duplicate
      if (timeDiff < 2000 && logAction === existingAction) {
        return true;
      }
      
      // Same path and method within 2 seconds = duplicate
      if (timeDiff < 2000 && log.path === existing.path && log.method === existing.method) {
        return true;
      }
      
      return false;
    });
    
    if (!isDuplicate) {
      acc.push(log);
    }
    return acc;
  }, []);
  
  const filteredLogs = deduplicatedLogs.filter(log => {
    const action = (log.action || '').toUpperCase();
    const path = log.path || '';
    
    if (activeTab === 'all') return true;
    
    if (activeTab === 'trade') {
      // Only show trade-related actions
      return ['CREATE', 'UPDATE', 'SEND', 'EXECUTE', 'FIX_EXECUTE', 'CANCEL', 'APPROVE', 'REJECT', 'VOID'].includes(action) 
        && !path.includes('/attachments') 
        && !path.includes('/notes');
    }
    
    if (activeTab === 'attachments') {
      return path.includes('/attachments') || action === 'ATTACHMENT';
    }
    
    if (activeTab === 'notes') {
      return path.includes('/notes') || action === 'NOTE';
    }
    
    return true;
  });
  
  const sortedLogs = [...filteredLogs].sort((a, b) => {
    const dateA = new Date(a.eventTime || a.createdAt || 0).getTime();
    const dateB = new Date(b.eventTime || b.createdAt || 0).getTime();
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });
  
  if (!isOpen) return null;
  
  return (
    <>
      <DrawerOverlay onClick={onClose} />
      <DrawerContainer>
        <DrawerHeader>
          <DrawerTitle>
            📋 <span>Audit History for Trade #{ticketNumber || tradeId}</span>
          </DrawerTitle>
          <CloseButton onClick={onClose}>Close</CloseButton>
        </DrawerHeader>
        
        <TabContainer>
          <Tab $active={activeTab === 'all'} onClick={() => setActiveTab('all')}>All</Tab>
          <Tab $active={activeTab === 'trade'} onClick={() => setActiveTab('trade')}>Trade</Tab>
          <Tab $active={activeTab === 'notes'} onClick={() => setActiveTab('notes')}>Notes</Tab>
          <Tab $active={activeTab === 'attachments'} onClick={() => setActiveTab('attachments')}>Attachments</Tab>
        </TabContainer>
        
        <SortContainer>
          <SortLabel>Sort By</SortLabel>
          <SortSelect 
            value={sortOrder} 
            onChange={(e) => setSortOrder(e.target.value as 'desc' | 'asc')}
          >
            <option value="desc">Newest First</option>
            <option value="asc">Oldest First</option>
          </SortSelect>
        </SortContainer>
        
        <AuditList>
          {loading && <LoadingState>Loading audit history...</LoadingState>}
          
          {error && <ErrorState>{error}</ErrorState>}
          
          {!loading && !error && sortedLogs.length === 0 && (
            <EmptyState>
              <div className="icon">📝</div>
              <div>No audit entries found</div>
            </EmptyState>
          )}
          
          {!loading && !error && sortedLogs.map((log) => (
            <AuditEntry key={log.id}>
              <AuditDate>{formatDateTime(log.eventTime || log.createdAt)}</AuditDate>
              <AuditUser>{log.username || 'System'}</AuditUser>
              <AuditAction>{formatAction(log)}</AuditAction>
              <AuditDescription>{formatDescription(log)}</AuditDescription>
            </AuditEntry>
          ))}
        </AuditList>
      </DrawerContainer>
    </>
  );
};

export default AuditHistoryDrawer;
